import { create } from 'zustand';
import { subscriptionsService } from '@/features/subscriptions/api';
import { SubscriptionPolicy } from '@/features/subscriptions/domain/subscription-policy';
import { env } from '@/config/env';
import type { UserSubscription } from '@/features/subscriptions/types';

export type VerificationStatus = 'idle' | 'verifying' | 'verified' | 'error';

/**
 * Local-access flag for the demo flow when no subscriptions backend is configured.
 * IMPORTANT: this is ONLY honored when the backend is not configured. In production
 * (backend configured) access is decided exclusively by the server response, so a
 * tampered flag here cannot unlock the app.
 */
const LOCAL_GRANT_KEY = 'bp-local-access';

const policy = new SubscriptionPolicy({ bundle: env.VITE_SUBSCRIPTION_BUNDLE });

function readLocalGrant(): boolean {
  try {
    return sessionStorage.getItem(LOCAL_GRANT_KEY) === 'true';
  } catch {
    return false;
  }
}

interface SubscriptionState {
  status: VerificationStatus;
  hasAccess: boolean;
  isCanceled: boolean;
  subscription: UserSubscription | null;
  error: string | null;
  /** Verify access against the backend (or local grant when backend is absent). */
  verify: (uid: string) => Promise<void>;
  /** Demo/no-backend fallback used by the post-purchase flow. */
  grantLocalAccess: () => void;
  /** Cancel the active subscription. Access remains until the period ends. */
  cancel: () => Promise<{ ok: boolean; error?: string }>;
  reset: () => void;
}

export const useSubscriptionStore = create<SubscriptionState>((set) => ({
  status: 'idle',
  hasAccess: false,
  isCanceled: false,
  subscription: null,
  error: null,

  verify: async (uid) => {
    if (!subscriptionsService.isConfigured) {
      set({
        status: 'verified',
        hasAccess: readLocalGrant(),
        subscription: null,
        isCanceled: false,
        error: null,
      });
      return;
    }

    set({ status: 'verifying', error: null });

    try {
      const subscriptions = await subscriptionsService.getUserSubscriptions(uid);
      const verdict = policy.evaluate(subscriptions);

      set({
        status: 'verified',
        hasAccess: verdict.hasAccess,
        isCanceled: verdict.isCanceled,
        subscription: verdict.subscription,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Subscription check failed';
      set({ status: 'error', hasAccess: false, error: message });
    }
  },

  grantLocalAccess: () => {
    try {
      sessionStorage.setItem(LOCAL_GRANT_KEY, 'true');
    } catch {
      /* ignore */
    }
    if (!subscriptionsService.isConfigured) {
      set({ status: 'verified', hasAccess: true });
    }
  },

  cancel: async () => {
    const { subscription } = useSubscriptionStore.getState();

    // Demo / no-backend: just reflect the canceled state locally.
    if (!subscriptionsService.isConfigured || !subscription) {
      set({ isCanceled: true });
      return { ok: true };
    }

    const res = await subscriptionsService.cancelSubscription(subscription.id);
    if (!res.success) {
      return { ok: false, error: res.error };
    }

    set({ isCanceled: true });
    return { ok: true };
  },

  reset: () => {
    try {
      sessionStorage.removeItem(LOCAL_GRANT_KEY);
    } catch {
      /* ignore */
    }
    set({
      status: 'idle',
      hasAccess: false,
      isCanceled: false,
      subscription: null,
      error: null,
    });
  },
}));
